/**
 * Adia a montagem do app até o navegador registrar a primeira pintura do HTML
 * que já veio pronto (prerender da home e das LPs, SSR do blog).
 *
 * O `createRoot` troca o conteúdo do `#root` pelo que o React desenha. Quando
 * o bundle chega rápido, isso acontecia logo depois da primeira pintura, antes
 * de o Chrome registrar o h1 do hero como candidato a LCP: o candidato do HTML
 * pré-renderizado era descartado e o LCP passava a ser o h1 recriado pelo
 * React, centenas de milissegundos depois (no Lighthouse mobile, 1,2 s contra
 * 6 s, conforme a ordem de chegada). Na tela não muda nada: o visitante já
 * via o h1 desde a primeira pintura.
 *
 * Espera a primeira entrada de `largest-contentful-paint` (com `buffered`,
 * pega a que já tiver saído), com teto de `timeoutMs` para aba em segundo
 * plano, que não pinta. Navegador sem a métrica (Safari) monta depois de
 * dois frames. `#root` vazio (app-shell da área logada) monta na hora.
 */
export function mountAfterFirstPaint(
  root: HTMLElement,
  mount: () => void,
  { timeoutMs = 1000 }: { timeoutMs?: number } = {}
): void {
  if (root.childElementCount === 0) {
    mount();
    return;
  }

  let done = false;
  const pending: {
    observer?: PerformanceObserver;
    timer?: ReturnType<typeof setTimeout>;
  } = {};

  const run = () => {
    if (done) return;
    done = true;
    pending.observer?.disconnect();
    if (pending.timer !== undefined) clearTimeout(pending.timer);
    mount();
  };

  const supportsLcp =
    typeof PerformanceObserver !== "undefined" &&
    (PerformanceObserver.supportedEntryTypes ?? []).includes("largest-contentful-paint");

  if (!supportsLcp) {
    requestAnimationFrame(() => requestAnimationFrame(run));
    pending.timer = setTimeout(run, timeoutMs);
    return;
  }

  const observer = new PerformanceObserver(list => {
    // Sai do callback antes de montar: a montagem é pesada e não precisa
    // rodar dentro da entrega da métrica.
    if (list.getEntries().length > 0) setTimeout(run, 0);
  });
  pending.observer = observer;
  observer.observe({ type: "largest-contentful-paint", buffered: true });
  pending.timer = setTimeout(run, timeoutMs);
}
